import axios from 'axios';
import qs from 'query-string';
import { clearStorageAndRedirect, isSessionExpired } from '../utils/global';
import {
  getTokenFromLocalStorage,
  getAuthToken,
  handleAuthLogout,
  errorHandler,
} from '../utils/handlers';
import reduxStore from '../store';
const isAzureMFAEnabled = process.env.REACT_APP_IS_AZURE_MFA_ENABLED;

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api/v1`,
  headers: {
    authentication: '',
  },
});

// True whenever this page is running as the Sales App's embedded map-search
// iframe: `?isSalesApp=true&token=...` on the URL, or (once PrivateRoute has
// dispatched it) the redux/localStorage user carrying isSalesApp. In this mode
// we must never touch MSAL or depend on storage roundtripping (3rd-party
// storage is blocked in iframes on current browsers), and never redirect to
// /login on failure — the URL query string is the only source guaranteed to
// be available synchronously and unaffected by iframe storage partitioning.
const getSalesAppAuth = () => {
  const urlParams = qs.parse(window.location.search);
  if (urlParams?.isSalesApp === 'true' && urlParams?.token) {
    return { isSalesApp: true, token: urlParams.token };
  }
  const reduxUser = reduxStore.getState()?.auth;
  if (reduxUser?.user?.isSalesApp && reduxUser?.token) {
    return { isSalesApp: true, token: reduxUser.token };
  }
  let storedUser = null;
  try {
    storedUser = JSON.parse(localStorage.getItem('user'));
  } catch {
    storedUser = null;
  }
  if (storedUser?.isSalesApp) {
    return { isSalesApp: true, token: getTokenFromLocalStorage() };
  }
  return { isSalesApp: false, token: null };
};

instance.interceptors.request.use(async config => {
  const customConfig = config;
  customConfig.headers = {
    'Content-Type': 'application/json',
  };

  if (config?.route !== 'public') {
    const salesAppAuth = getSalesAppAuth();
    let token;

    if (salesAppAuth.isSalesApp) {
      token = salesAppAuth.token;
    } else {
      token =
        isAzureMFAEnabled?.toLowerCase() === 'true'
          ? await getAuthToken()
          : getTokenFromLocalStorage();

      if (!token) clearStorageAndRedirect();

      if (isSessionExpired(token) && config.url !== '/sign_in') {
        clearStorageAndRedirect();
      }
    }

    if (config.url !== '/sign_in' && token) {
      customConfig.headers.Authorization = `Bearer ${token}`;
    }
  }
  return customConfig;
});

instance.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error?.response?.status === 401) {
      if (getSalesAppAuth().isSalesApp) {
        errorHandler(
          error?.response?.data?.message ||
            'Unable to load map data. Please try again.'
        );
      } else {
        isAzureMFAEnabled?.toLowerCase() !== 'true'
          ? clearStorageAndRedirect()
          : setTimeout(() => {
              handleAuthLogout();
            }, 3000);
      }
    }
    return Promise.reject(error);
  }
);

export default instance;
